import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Variables from '../../design-system/variables';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import OvButton from '../UI/atoms/OvButton';
import OvTable from '../UI/molecules/OvTable';
import { NotificationContentDataUtils } from '../../common/utils/services/notification-content-utils';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvPublishNotificationContentDialog from '../UI/molecules/OvPublishNotificationContentDialog';
import { PublishNotificationRequest } from '../../common/model/dto/notification/publish-notification-request';
import { createNotifications } from '../../services/admin/admin-notification.service';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  deleteNotificationContent,
  getNotificationContentById,
  updateNotificationContent,
} from '../../services/admin/admin-notification-content.service';
import OvNotificationContentDevicePreview from '../UI/molecules/OvNotificationContentDevicePreview';
import OvDeviceView from '../UI/molecules/OvDeviceView';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import OvBackButton from '../UI/atoms/OvBackButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UpdateNotificationContentRequest } from '../../common/model/dto/notification/update-notification-content-request';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import OvCreateSymptomLoggedEventNotificationContentDialog from '../UI/molecules/OvCreateSymptomLoggedEventNotificationContentDialog';
import OvNotificationContentDialog from '../UI/molecules/OvCreateNotificationsContentDialog';
import { NotificationContentTabType } from '../../common/model/type/notification-content-tab.type';
import ResponsiveActions from '../UI/molecules/ResponsiveActions';

const NotificationContentDetails = () => {
  const { category, notificationContentId }: any = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);

  const {
    data: notificationContentResponse,
    isPending: isGetNotificationContentPending,
    isFetching: isGetNotificationContentFetching,
    error: getNotificationContentError,
    refetch,
  } = useQuery({
    queryKey: ['admin-notification-content', notificationContentId],
    queryFn: () => getNotificationContentById(notificationContentId),
    retry: 3,
    enabled: !!notificationContentId,
  });

  const {
    mutate: updateNotificationContentMutation,
    isPending: isUpdateNotificationContentPending,
    error: updateNotificationContentError,
  } = useMutation({
    mutationFn: (
      updateNotificationContentRequest: UpdateNotificationContentRequest
    ) =>
      updateNotificationContent(
        notificationContentId,
        updateNotificationContentRequest
      ),
    onSuccess: () => {
      refetch();
    },
  });

  const {
    mutate: publishNotificationContentMutation,
    isPending: isPublishNotificationContentPending,
    error: publishNotificationContentError,
  } = useMutation({
    mutationFn: (
      publishNotificationContentRequest: PublishNotificationRequest
    ) =>
      createNotifications(
        notificationContentId,
        publishNotificationContentRequest
      ),
    onSuccess: () => {
      refetch();
    },
  });

  const {
    mutate: deleteNotifciationContentMutation,
    isPending: isDeleteNotificationContentPending,
    error: deleteNotificationContentError,
  } = useMutation({
    mutationFn: () => deleteNotificationContent(notificationContentId),
    onSuccess: () => {
      navigate('/notification-contents');
    },
  });

  const notificationContentOperations = useAuthorized(
    Resource.NotificationContent
  );

  const notificationContentUpdateAllowed =
    notificationContentOperations.update ||
    notificationContentOperations.supervise;

  const isLoading =
    isGetNotificationContentPending ||
    isGetNotificationContentFetching ||
    isUpdateNotificationContentPending ||
    isDeleteNotificationContentPending ||
    isPublishNotificationContentPending;

  return (
    <DetailPageWrapper>
      <DetailsHeader>
        <DetailsWrapper>
          <OvBackButton label={t('common.actions.back')} />
          <NotificationContentTitle>
            {notificationContentResponse?.title}
          </NotificationContentTitle>
        </DetailsWrapper>

        <ResponsiveActions>
          {notificationContentUpdateAllowed && (
            <StyledOvButton onClick={() => setIsEditDialogOpen(true)}>
              <EditIcon style={{ marginRight: '0.5rem' }} />
              {t('notificationContents.dialogs.editNotificationContent')}
            </StyledOvButton>
          )}

          {!notificationContentResponse?.event_type && (
            <StyledOvButton
              disabled={notificationContentResponse?.is_published}
              onClick={() => setIsDialogOpen(true)}
            >
              <SendIcon style={{ marginRight: '0.5rem' }} />
              {t('notificationContents.publishNotificationContent')}
            </StyledOvButton>
          )}

          <StyledOvButton onClick={() => setIsConfirmationDialogOpen(true)}>
            <DeleteIcon style={{ marginRight: '0.5rem' }} />
            {t('notificationContents.deleteNotificationContent')}
          </StyledOvButton>
        </ResponsiveActions>
      </DetailsHeader>

      <span>
        {getNotificationContentError && (
          <OvErrorMessage message={getNotificationContentError.message} />
        )}
        {updateNotificationContentError && (
          <OvErrorMessage message={updateNotificationContentError.message} />
        )}
        {deleteNotificationContentError && (
          <OvErrorMessage message={deleteNotificationContentError.message} />
        )}
        {publishNotificationContentError && (
          <OvErrorMessage message={publishNotificationContentError.message} />
        )}
      </span>

      <DetailPageContentWrapper>
        <TableWrapper>
          <OvTable
            data={NotificationContentDataUtils.mapNotificationContentToTableDataFormat(
              notificationContentResponse
            )}
          />
        </TableWrapper>
        {notificationContentResponse && (
          <DeviceViewContainer>
            <OvDeviceView>
              <OvNotificationContentDevicePreview
                notificationContentDetails={notificationContentResponse}
              />
            </OvDeviceView>
          </DeviceViewContainer>
        )}
      </DetailPageContentWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}

      <OvPublishNotificationContentDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={publishNotificationContentMutation}
      />

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isConfirmationDialogOpen}
        onCancel={() => setIsConfirmationDialogOpen(false)}
        onConfirm={deleteNotifciationContentMutation}
        description="notificationContents.dialogs.deleteNotificationContentDescription"
        title="notificationContents.dialogs.deleteNotificationContentTitle"
      />

      {category === NotificationContentTabType.SYMPTOMS &&
        notificationContentResponse &&
        isEditDialogOpen && (
          <OvCreateSymptomLoggedEventNotificationContentDialog
            isOpen={isEditDialogOpen}
            onCancel={() => setIsEditDialogOpen(false)}
            onSave={updateNotificationContentMutation}
            notificationContent={notificationContentResponse}
          />
        )}

      {category !== NotificationContentTabType.SYMPTOMS &&
        notificationContentResponse &&
        isEditDialogOpen && (
          <OvNotificationContentDialog
            tabType={category as NotificationContentTabType}
            isOpen={isEditDialogOpen}
            onCancel={() => setIsEditDialogOpen(false)}
            onSave={updateNotificationContentMutation}
            notificationContent={notificationContentResponse}
          />
        )}
    </DetailPageWrapper>
  );
};

export default NotificationContentDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const DetailsHeader = styled.header`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-weight: bold;
  justify-content: space-between;
`;

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  margin-left: 12px;
  width: 100%;
  @media (max-width: ${breakpoints.md}) {
    margin-left: 0;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

const NotificationContentTitle = styled.h6`
  margin: 0;
  font-weight: bold;
  font-size: ${Variables.fontSizes.LARGE};
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: none;
    padding: 2px 10px;
    padding: 2px 0.625rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    background-color: ${Colours.OV_BASE};

    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledDeleteIcon = styled(DeleteSweepIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const DetailPageContentWrapper = styled.div`
  display: flex;
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;

const DeviceViewContainer = styled.div`
  min-width: 24rem;
  max-width: 100%;
  height: 41rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem;

  @media (max-width: ${breakpoints.md}) {
    /* make it full-width and reset the min-width */
    min-width: auto;
    width: 100%;
    margin: 1rem 0;
  }
`;
